import { useEffect, useState } from "react";
import { Check, Copy, Loader2, MessageSquare, Send, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export const ShareStory = ({ story, onClose }) => {
  const { languages, lang } = useI18n();
  const [to, setTo] = useState(lang || "en");
  const [note, setNote] = useState("");
  const [making, setMaking] = useState(false);
  const [made, setMade] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");

  useEffect(() => {
    if (!made) return;
    api.get("/rooms").then(({ data }) => {
      setRooms(data);
      setRoom((r) => r || data[0]?.room_id || "");
    }).catch(() => {});
  }, [made]);

  const sendInChat = async () => {
    if (!room || sending) return;
    setSending(true);
    try {
      await api.post(`/rooms/${room}/messages`, {
        text: `${made.title}\n${made.link}`,
      });
      const name = rooms.find((r) => r.room_id === room)?.name || "your chat";
      setSentTo(name);
      toast.success(`Sent to ${name} — everyone reads it in their own language`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send it in that chat");
    } finally {
      setSending(false);
    }
  };

  const make = async () => {
    setMaking(true);
    try {
      const { data } = await api.post("/news/share", {
        story_id: story.story_id || "", title: story.title, url: story.url,
        source: story.source || "", snippet: story.snippet || "", lang: to, note: note.trim(),
      });
      setMade(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not make that card");
    } finally {
      setMaking(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made.link);
      setCopied(true);
      toast.success("Link copied — paste it to your friend");
    } catch {
      toast.error("Could not copy — long-press the link instead");
    }
  };

  const send = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title: made.title, text: made.blurb || made.title, url: made.link });
    } catch {
      /* the sheet was dismissed */
    }
  };

  const langName = (code) => languages.find((l) => l.code === code)?.name || code;

  return (
    <div className="mt-3 rounded-2xl border border-brand/30 bg-brand/5 p-3"
         data-testid={`share-panel-${story.story_id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand">
          <Share2 className="h-3.5 w-3.5" /> Send it to a friend
        </p>
        <button onClick={onClose} data-testid={`share-close-${story.story_id}`}
                className="text-ink-soft transition-colors hover:text-ink">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {!made ? (
        <>
          <p className="mt-2 text-[11px] text-ink-soft">
            MiLu writes the headline and the summary in their language, on a card they can open anywhere.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              data-testid={`share-lang-${story.story_id}`}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="min-h-[34px] rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
            <input
              data-testid={`share-note-${story.story_id}`}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              placeholder="A short note (optional)"
              className="min-h-[34px] min-w-0 flex-1 rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
            />
            <button
              data-testid={`share-make-${story.story_id}`}
              onClick={make}
              disabled={making}
              className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
            >
              {making ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {making ? "Writing it…" : "Make the card"}
            </button>
          </div>
        </>
      ) : (
        <div className="mt-2" data-testid={`share-made-${story.story_id}`}>
          <img src={made.card} alt="" loading="lazy"
               className="w-full rounded-xl border border-white/10"
               data-testid={`share-card-${story.story_id}`} />
          <p className="mt-2 text-sm font-semibold" dir="auto">{made.title}</p>
          <p className="mt-0.5 text-[11px] text-ink-soft">In {langName(made.lang)}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              data-testid={`share-copy-${story.story_id}`}
              onClick={copy}
              className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-brand/40 px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand/10"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy link"}
            </button>
            <button
              data-testid={`share-send-${story.story_id}`}
              onClick={send}
              className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full bg-brand px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-hover"
            >
              <Send className="h-3.5 w-3.5" /> Share
            </button>
            <a
              href={made.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`share-open-${story.story_id}`}
              className="text-xs text-ink-soft underline decoration-dotted hover:text-ink"
            >
              See what they'll see
            </a>
          </div>

          <div className="mt-3 border-t border-white/10 pt-3" data-testid={`share-chat-${story.story_id}`}>
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-ink-soft">
              <MessageSquare className="h-3 w-3 text-brand" /> Or send it straight into a chat
            </p>
            {rooms.length === 0 ? (
              <p className="mt-1 text-[11px] text-ink-soft" data-testid={`share-chat-empty-${story.story_id}`}>
                Start a chat in iMessages and it will show up here.
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  data-testid={`share-room-${story.story_id}`}
                  value={room}
                  onChange={(e) => setRoom(e.target.value)}
                  className="min-h-[32px] rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
                >
                  {rooms.map((r) => (
                    <option key={r.room_id} value={r.room_id}>{r.name}</option>
                  ))}
                </select>
                <button
                  data-testid={`share-to-room-${story.story_id}`}
                  onClick={sendInChat}
                  disabled={sending || !room}
                  className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full bg-brand px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <MessageSquare className="h-3.5 w-3.5" />}
                  {sending ? "Sending…" : "Send in iMessages"}
                </button>
                {sentTo && (
                  <span className="text-[11px] text-brand" data-testid={`share-sent-${story.story_id}`}>
                    Sent to {sentTo}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
